/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get } from 'lodash';
import { debounce } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import AsyncSelect from '../../components/AsyncSelect';
import Button from '../../components/Button';
import UserThumbnail from '../../components/UserThumbnail/UserThumbnail';
import AuthService from '../../services/auth';
import { uploadFile } from '../../../utils/s3';
import '../SignIn/SignIn.scss';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const authService = AuthService();

export default function SetProfile({ onServerError = () => {} }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: '', last_name: '', phone: '', city_id: null, user_pic: '',
  });
  const fileInputRef = useRef(null);

  const tr = (key) => t(`common.pages.set_profile.${key}`);

  const validationSchema = Yup.object({
    first_name: Yup.string().trim().required(tr('errors.first_name_required')),
    last_name: Yup.string().trim().required(tr('errors.last_name_required')),
    phone: Yup.string()
      .required(tr('errors.phone_required'))
      .test('valid-phone', tr('errors.phone_invalid'),
        (value) => (value || '').replace(/\D/g, '').length >= 10),
    city_id: Yup.number().required(tr('errors.city_required')).nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      onServerError(false);

      axios.post('/api/v1/influencers/users/set_initial_profile', { user: values })
        .then(({ data }) => {
          if (data && data.success) {
            window.location.href = '/manager/home';
            return;
          }
          handleFailure(get(data, 'errors'));
          setLoading(false);
        })
        .catch((error) => {
          const status = get(error, 'response.status');
          if (status === 422) {
            handleFailure(get(error, 'response.data.errors'));
          } else {
            onServerError(true);
          }
          setLoading(false);
        });
    },
  });

  const handleFailure = (errors) => {
    if (!errors) {
      onServerError(true);
      return;
    }
    let handled = false;
    ['first_name', 'last_name', 'phone', 'city_id'].forEach((field) => {
      const message = get(errors, `${field}.0`);
      if (message) {
        formik.setFieldError(field, message);
        formik.setFieldTouched(field, true, false);
        handled = true;
      }
    });
    if (!handled) onServerError(true);
  };

  const onLoadCities = (searchCity) => {
    if (!searchCity || searchCity.length < 3) return undefined;
    return new Promise((resolve) => {
      axios.get(`/api/v1/cities?query=${searchCity}`)
        .then(({ data }) => {
          resolve(data.cities.map((city) => ({ ...city, value: city.id })));
        });
    });
  };

  const debouncedLoadCities = useRef(debounce(onLoadCities, 300)).current;
  useEffect(() => () => debouncedLoadCities.cancel(), [debouncedLoadCities]);

  useEffect(() => {
    let active = true;
    authService.getUser().then((userData) => {
      if (!active || !userData) return;
      setInitialValues({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        phone: userData.phone || '',
        city_id: userData.city_id || null,
        user_pic: '',
      });
    });
    return () => { active = false; };
  }, []);

  const onPhotoSelected = (event) => {
    const file = get(event, 'target.files.0');
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES) {
      formik.setFieldError('user_pic', tr('errors.photo_invalid'));
      return;
    }

    formik.setFieldError('user_pic', undefined);
    setUploading(true);
    uploadFile(file, () => {}, { checkAvifOnly: false })
      .then((response) => {
        if (get(response, 'data.success') === false || !get(response, 'data.url')) {
          formik.setFieldError('user_pic', tr('errors.photo_invalid'));
        } else {
          formik.setFieldValue('user_pic', response.data.url);
        }
        setUploading(false);
      })
      .catch(() => {
        formik.setFieldError('user_pic', tr('errors.photo_invalid'));
        setUploading(false);
      });
  };

  const renderField = ({ name, label, placeholder, type = 'text', autoComplete }) => (
    <Form.Group className='mb-3'>
      <Form.Label htmlFor={name}>{label}</Form.Label>
      <Form.Control
        type={type}
        id={name}
        name={name}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[name]}
        isInvalid={Boolean(formik.touched[name] && formik.errors[name])} />
      <Form.Text className='text-muted danger-text'>
        {formik.touched[name] && formik.errors[name]}
      </Form.Text>
    </Form.Group>
  );

  return <Form onSubmit={formik.handleSubmit}>
    <Card>
      <Card.Header>
        <h3 className='mb-sm-4'>{tr('title')}</h3>
        <div className='flex flex-column align-items-center'>
          <UserThumbnail
            imageUrl={formik.values.user_pic}
            initials={formik.values.first_name}
            size='xl'
            gradient />
          <input
            ref={fileInputRef}
            type='file'
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            className='d-none'
            onChange={onPhotoSelected} />
          <Button
            variant='link'
            type='button'
            disabled={uploading}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            {uploading ? <Spinner animation='border' size='sm' /> : tr('upload_photo')}
          </Button>
          {formik.errors.user_pic && (
            <Form.Text className='text-muted danger-text'>
              {formik.errors.user_pic}
            </Form.Text>
          )}
        </div>
      </Card.Header>
      <Card.Body className='mb-4-5'>
        {renderField({
          name: 'first_name',
          label: tr('first_name'),
          placeholder: tr('first_name_placeholder'),
          autoComplete: 'given-name',
        })}
        {renderField({
          name: 'last_name',
          label: tr('last_name'),
          placeholder: tr('last_name_placeholder'),
          autoComplete: 'family-name',
        })}
        {renderField({
          name: 'phone',
          label: tr('phone'),
          placeholder: tr('phone_placeholder'),
          type: 'tel',
          autoComplete: 'tel',
        })}
        <Form.Group className='mb-3'>
          <Form.Label>{tr('city')}</Form.Label>
          <AsyncSelect
            type='text'
            name='city_id'
            cacheOptions
            placeholder={tr('city_placeholder')}
            onChange={formik.handleChange}
            onMenuClose={() => formik.setFieldTouched('city_id', true)}
            value={formik.values.city_id}
            error={Boolean(formik.touched.city_id && formik.errors.city_id)}
            loadOptions={debouncedLoadCities}
            isClearable />
          <Form.Text className='text-muted danger-text'>
            {formik.touched.city_id && formik.errors.city_id}
          </Form.Text>
        </Form.Group>
      </Card.Body>
      <hr />
      <Card.Footer>
        <div className='flex flex-column align-items-end'>
          <Button
            id='set-profile-button'
            variant='primary'
            type='submit'
            disabled={loading || uploading}>
            {loading ? <Spinner animation='border' size='sm' /> : tr('submit')}
          </Button>
        </div>
      </Card.Footer>
    </Card>
  </Form>;
}

SetProfile.propTypes = {
  onServerError: PropTypes.func,
};
